import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Send, CheckCircle2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { INTEREST_OPTIONS } from "@/lib/constants";

export function LeadForm({ scanId, businessName, healthScore, prefill }) {
  const [form, setForm] = useState({
    name: "", business_name: businessName || "", email: "", phone: "",
    interest: "Full business audit", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (prefill?.message !== undefined) {
      setForm((f) => ({ ...f, message: prefill.message, interest: prefill.interest || f.interest }));
    }
  }, [prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business_name || !form.email) {
      toast.error("Please add your name, business name and email.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/leads", { ...form, scan_id: scanId });
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div id="lead-capture" initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5 }}
      className="rounded-2xl bg-slate-900 text-white overflow-hidden" data-testid="lead-form">
      <div className="grid lg:grid-cols-5">
        <div className="lg:col-span-2 p-8 sm:p-10 relative">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 text-indigo-300 font-semibold text-xs uppercase tracking-[0.18em] mb-4">
              <ShieldCheck className="w-4 h-4" /> Act on your biggest opportunities
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight leading-snug">
              Have our team fix these opportunities for you.
            </h2>
            <p className="mt-4 text-slate-300 text-sm leading-relaxed">
              <span className="font-semibold text-white">Why we ask:</span> so a specialist can review your scan,
              confirm what's worth fixing first, and prepare a tailored recovery plan for your business. We only use
              your details to contact you about these findings.
            </p>
            <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
              <Lock className="w-3.5 h-3.5" /> Your information is kept private and never sold.
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 bg-white text-slate-900 p-8 sm:p-10">
          {done ? (
            <div className="h-full grid place-items-center text-center py-8" data-testid="lead-success">
              <div>
                <span className="grid place-items-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7" />
                </span>
                <h3 className="font-heading text-xl font-semibold text-slate-900">Request received</h3>
                <p className="mt-2 text-sm text-slate-500 max-w-xs">
                  Thanks, {form.name.split(" ")[0] || "there"}. Our team will reach out about fixing the
                  opportunities found for {form.business_name}.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Your name *</Label>
                  <Input data-testid="lead-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Owner" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Business name *</Label>
                  <Input data-testid="lead-business" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Your business" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Email *</Label>
                  <Input data-testid="lead-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@business.com" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Input data-testid="lead-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(808) 555-1212" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">What do you need help with?</Label>
                <Select value={form.interest} onValueChange={(v) => set("interest", v)}>
                  <SelectTrigger data-testid="lead-interest"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INTEREST_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Message <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Textarea data-testid="lead-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us a bit about your goals…" />
              </div>
              <button type="submit" data-testid="lead-submit" disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 transition-colors active:scale-[0.99]">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request My Fix Plan <Send className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </motion.div>
  );
}
